/*
 * <tool-tip> and <tool-tip-trigger> Web Components.
 * Extracted from legacy theme.js. Consumed by:
 *   - layout/theme.liquid renders {% render 'tool-tip' %} once globally
 *   - snippets/tool-tip-trigger.liquid wraps a trigger element
 *
 * Interaction: clicking a <tool-tip-trigger> dispatches `tooltip:open` on
 * document; the global <tool-tip> listens for it, swaps in content, traps
 * focus until clicked outside / ESC pressed.
 */

import { trapFocus, removeTrapFocus } from '@/components/a11y';

class ToolTip extends HTMLElement {
  constructor() {
    super();
    this.inner = this.querySelector('[data-tool-tip-inner]');
    this.closeButton = this.querySelector('[data-tool-tip-close]');
    this.toolTipContent = this.querySelector('[data-tool-tip-content]');
    this._onOpen = this._onOpen.bind(this);
    this._onOuterClick = this._onOuterClick.bind(this);
    this._onEsc = this._onEsc.bind(this);
    this._onCloseClick = this._close.bind(this);
  }

  connectedCallback() {
    document.addEventListener('tooltip:open', this._onOpen);
  }

  disconnectedCallback() {
    document.removeEventListener('tooltip:open', this._onOpen);
  }

  _onOpen(e) {
    if (!e.detail) return;
    this.toolTipContent.innerHTML = e.detail.content;
    trapFocus({ container: this, namespace: 'tooltip_focus' });

    if (this.closeButton) this.closeButton.addEventListener('click', this._onCloseClick);
    document.documentElement.addEventListener('click', this._onOuterClick);
    document.documentElement.addEventListener('keydown', this._onEsc);

    this.dataset.toolTipOpen = 'true';
    this.dataset.toolTip = e.detail.context;
  }

  _onOuterClick(event) {
    if (this.dataset.toolTipOpen === 'true' && !this.inner.contains(event.target)) this._close();
  }

  _onEsc(event) {
    if (event.code === 'Escape') this._close();
  }

  _close() {
    this.toolTipContent.innerHTML = '';
    this.dataset.toolTipOpen = 'false';
    this.dataset.toolTip = '';
    removeTrapFocus({ container: this, namespace: 'tooltip_focus' });
    if (this.closeButton) this.closeButton.removeEventListener('click', this._onCloseClick);
    document.documentElement.removeEventListener('click', this._onOuterClick);
    document.documentElement.removeEventListener('keydown', this._onEsc);
  }
}

class ToolTipTrigger extends HTMLElement {
  constructor() {
    super();
    this.toolTipContent = this.querySelector('[data-tool-tip-content]');
    this._onClick = this._onClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener('click', this._onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._onClick);
  }

  _onClick(e) {
    e.stopPropagation();
    this.dispatchEvent(
      new CustomEvent('tooltip:open', {
        detail: {
          context: this.dataset.toolTip,
          content: this.toolTipContent ? this.toolTipContent.innerHTML : '',
        },
        bubbles: true,
      })
    );
  }
}

if (!customElements.get('tool-tip')) customElements.define('tool-tip', ToolTip);
if (!customElements.get('tool-tip-trigger')) customElements.define('tool-tip-trigger', ToolTipTrigger);

export { ToolTip, ToolTipTrigger };
